import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, of, Subscription, switchMap, take } from 'rxjs';
import { KbDataExtractionRequestService } from 'src/app/services/feature/data-extraction-request/kb-data-extraction-request.service';
import { RegisterService } from 'src/app/services/feature/register.service';

@Component({
  selector: 'app-kb-requestor',
  templateUrl: './kb-requestor.component.html',
  styleUrl: './kb-requestor.component.scss',
  standalone: false
})
export class KbRequestorComponent implements OnInit, OnDestroy {
  private dataExtractionService = inject(KbDataExtractionRequestService);
  private fb = inject(FormBuilder);
  private registerService = inject(RegisterService);
  private cdr = inject(ChangeDetectorRef);

  sameAsAbove: boolean = false;
  allowNext = false;
  isAutoPopulated = false;

  pageFieldData: any;
  pageFieldData$ = this.registerService.getRegisterPageFields();
  accessRequestsWSList: any;
  countriesWSList: any;
  stateList: any;

  subscription = new Subscription();

  popupOpenCompanyName = false;
  popupOpenContactName = false;

  requestorForm = this.fb.group({
    companyName: ['', Validators.required],
    contactName: ['', Validators.required],
    contactEmail: ['', [Validators.required, Validators.email]],
    contactPhone: ['', Validators.required],
    address1: ['', Validators.required],
    address2: [''],
    city: ['', Validators.required],
    state: [null as string | null, Validators.required],
    country: [null as string | null, Validators.required],
    zip: ['', Validators.required],
  });
  private timeout?: ReturnType<typeof setTimeout> | null = null;// Store the timeout ID
  constructor() { }

  ngOnInit(): void {
    this.setupCountryAndState();
    this.subscription.add(
      this.dataExtractionService.requestorDataCheckeBox$.subscribe((dataChecbox) => {
        this.sameAsAbove = dataChecbox;
        if (!this.sameAsAbove) {
          this.isAutoPopulated = false;
          this.allowNext = false;
        }
        else {
          this.isAutoPopulated = true;
          this.allowNext = true;
        }
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.dataExtractionService.requestorData$.subscribe((data) => {
        this.setupForm(data);
      })
    );
    this.subscription.add(
      this.requestorForm.statusChanges.subscribe(status => {
        // Allow Next only if form is valid OR auto-populated by checkbox
        this.allowNext = (status === 'VALID') || this.isAutoPopulated;
        console.log(this.allowNext);
      })
    );
  }
  setupCountryAndState() {
    forkJoin({
      countrydata: this.registerService.getRegisterPageFields().pipe(take(1)),
      stateData: this.registerService.getRegions('US').pipe(take(1))
    }).subscribe({
      next: ({ countrydata, stateData }) => {
        this.pageFieldData = countrydata;
        this.countriesWSList = this.pageFieldData.countriesWSList;
        this.stateList = stateData;
        this.timeout = setTimeout(() => {
          this.requestorForm.get('country')?.setValue('US', { emitEvent: false });
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        console.error('Failed to load register fields/regions:', err);
      }
    });
  }
  back() {
    this.dataExtractionService.setRequestorDataCheckbox(this.sameAsAbove);
    const payload = this.requestorForm.getRawValue?.() || this.requestorForm.value;
    this.dataExtractionService.setRequestorData(payload);
    this.dataExtractionService.currentComponent$.next('customerVehicleOwner');
  }

  next() {
    const payload = this.requestorForm.getRawValue?.() || this.requestorForm.value;
    this.dataExtractionService.setRequestorData(payload);
    this.dataExtractionService.currentComponent$.next('reportECURecipient');
  }

  // ------------------------------------------------------------
  //  FIXED — Country Change — does NOT reset state
  // ------------------------------------------------------------
  onCountryChange(event: any, preSelectedState: string | null = null) {
    const rawdatacountryIso = event?.target?.value || event;
    const countryIso = rawdatacountryIso.split(":")[1].trim();
    if (!countryIso) {
      this.stateList = null;
      return;
    }
    this.registerService.getRegions(countryIso).pipe(take(1)).subscribe((data) => {
      this.stateList = data;
      //  If a state was provided, set AFTER loading
      if (preSelectedState) {
        this.timeout = setTimeout(() => {
          this.requestorForm.get('state')?.setValue(preSelectedState, { emitEvent: false });
        });
      }
    });
  }

  // ------------------------------------------------------------
  //  FIXED — Checkbox auto populate
  // ------------------------------------------------------------
  setupFormValuesOnCheckboxClick(event: any) {
    const checked = !!event?.target?.checked;
    this.sameAsAbove = checked;
    this.dataExtractionService.setRequestorDataCheckbox(checked);
    if (!checked) {
      this.isAutoPopulated = false;
      this.allowNext = false;
      this.requestorForm.reset();
      this.requestorForm.enable();
      this.setupCountryAndState();
      this.requestorForm.updateValueAndValidity();
      return;
    }

    this.subscription.add(this.dataExtractionService.customerData$.pipe(take(1),
      switchMap((customerData) => {
        if (!customerData) {
          return of({ customerData: null, stateData: null });
        }
        const { state, ...rest } = customerData;
        this.requestorForm.patchValue(rest, { emitEvent: false });
        const countryIso = customerData.country;

        // load state regions
        return this.registerService.getRegions(countryIso).pipe(take(1), switchMap((stateData) => of({ customerData, stateData })));
      })).subscribe(({ customerData, stateData }: any) => {
        if (!customerData) {
          this.allowNext = false;
          return;
        }

        this.stateList = stateData;
        const stateIso = customerData.state;
        // ⭐ Set state AFTER regions load
        this.timeout = setTimeout(() => {
          const validState = this.stateList?.regionsWSList?.some(
            (x: any) => x.isocode === stateIso
          );

          this.requestorForm.get('state')?.setValue(validState ? stateIso : null, { emitEvent: false });

          // disable UI
          this.requestorForm.disable();
          this.requestorForm.updateValueAndValidity({ emitEvent: false });

          // enable next button
          this.allowNext = true;
          this.isAutoPopulated = true;
          this.cdr.detectChanges();
        });
      })
    );
  }

  // ------------------------------------------------------------
  // ⭐ FIXED — Existing Form Setup (edit mode)
  // ------------------------------------------------------------
  setupForm(data: any) {
    const countryIso = data?.country;
    const stateIso = data?.state;

    const { state, ...rest } = data;
    this.requestorForm.patchValue(rest);

    this.registerService
      .getRegions(countryIso)
      .pipe(take(1))
      .subscribe((stateData: any) => {
        this.stateList = stateData;

        const isValid = this.stateList?.regionsWSList?.some(
          (x: any) => x.isocode === stateIso
        );

        this.timeout = setTimeout(() => {
          this.requestorForm
            .get('state')
            ?.setValue(isValid ? stateIso : null, { emitEvent: false });

          if (this.sameAsAbove) {
            this.requestorForm.disable();
          }
          this.requestorForm.updateValueAndValidity();
          this.isAutoPopulated = true;
          this.allowNext = true;
        });
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
  openPopupCompanyName() {
    this.popupOpenCompanyName = true;
  }

  closePopupCompanyName() {
    this.popupOpenCompanyName = false;
  }

  openPopupContactName() {
    this.popupOpenContactName = true;
  }

  closePopupContactName() {
    this.popupOpenContactName = false;
  }
}
